import { FC, Fragment } from "react";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CheckSmall } from "src/icons/CheckSmall";
import { StepperArrow } from "src/icons/StepperArrow";
import {
  StepperBadge,
  StepperItemState,
  StepperItemWrapper,
  StepperSeparator,
  StepperWrapper,
} from "./Stepper.styled";

export type Step = 1 | 2 | 3;

/** The check glyph is inset inside the 20px badge, per the Ozone spec. */
const CHECK_SIZE = 13.33;

const LAST_STEP: Step = 3;

const getStepState = (step: Step, activeStep: Step): StepperItemState => {
  if (step < activeStep) {
    return "completed";
  }

  return step === activeStep ? "current" : "upcoming";
};

/**
 * A step earns its checkmark once it is behind the user. Reaching the last
 * step means the whole flow is done, so it is checked while it is still the
 * active one — the label keeps the current-step emphasis either way.
 */
const isStepComplete = (step: Step, activeStep: Step): boolean =>
  step < activeStep || (step === LAST_STEP && activeStep === LAST_STEP);

const StepperItem: FC<{
  step: Step;
  label: string;
  state: StepperItemState;
  isComplete: boolean;
}> = ({ step, label, state, isComplete }) => (
  <StepperItemWrapper
    $state={state}
    aria-current={state === "current" ? "step" : undefined}
    data-testid={`stepper-step-${step}`}
    data-state={state}
  >
    <StepperBadge $state={state} aria-hidden="true">
      {isComplete ? (
        <CheckSmall
          width={CHECK_SIZE}
          height={CHECK_SIZE}
          fill="currentColor"
        />
      ) : (
        step
      )}
    </StepperBadge>
    {label}
  </StepperItemWrapper>
);

/**
 * Checkout progress indicator. Purely informational — the steps are never
 * links, so the user can only move forward through the flow.
 */
const Stepper: FC<{ activeStep: Step }> = ({ activeStep }) => {
  const { i18n } = useLingui();

  const steps: { step: Step; label: string }[] = [
    { step: 1, label: t(i18n)`Information` },
    { step: 2, label: t(i18n)`Payment` },
    { step: 3, label: t(i18n)`Confirmation` },
  ];

  return (
    <StepperWrapper data-testid="stepper-flow">
      {steps.map(({ step, label }, index) => (
        <Fragment key={step}>
          {index > 0 && (
            <StepperSeparator aria-hidden="true">
              <StepperArrow />
            </StepperSeparator>
          )}
          <StepperItem
            step={step}
            label={label}
            state={getStepState(step, activeStep)}
            isComplete={isStepComplete(step, activeStep)}
          />
        </Fragment>
      ))}
    </StepperWrapper>
  );
};

export default Stepper;
